/**
 * 设置滚动显现动画，默认针对 data-scroll-reveal 元素。
 * @param {HTMLElement} container - 观察的容器节点
 * @param {Object} [options]
 * @param {string} [options.selector='[data-scroll-reveal]'] - 目标元素选择器
 * @param {NodeList|HTMLElement[]} [options.targets] - 自定义目标集合
 * @param {string} [options.visibleClass='scroll-reveal-visible'] - 显现时添加的类名
 * @param {boolean} [options.once=true] - 是否只触发一次
 * @param {number} [options.threshold=0.35] - IntersectionObserver threshold
 * @param {number} [options.fallbackOffset=0.9] - 无 IO 时触发阈值（视口高度比例）
 * @param {number} [options.stagger=120] - 阶梯延迟（ms），设置为 0 关闭
 * @param {boolean} [options.guardForward=true] - 仅在向下滚动经过时触发
 * @param {(targets: HTMLElement[]) => void} [options.onReveal] - 显现后回调
 * @returns {() => void | null} 清理函数
 */
export function setupScrollReveal(
  container,
  {
    selector = '[data-scroll-reveal]',
    targets,
    visibleClass = 'scroll-reveal-visible',
    once = true,
    threshold = 0.35,
    fallbackOffset = 0.9,
    stagger = 120,
    guardForward = true,
    onReveal
  } = {}
) {
  if (!container) return null

  const revealNodes = Array.from(
    targets || container.querySelectorAll(selector)
  )

  if (!revealNodes.length) return null

  let hasRevealed = false
  let observer = null
  let cleanupFallback = null
  const delayStep = typeof stagger === 'number' && stagger > 0 ? stagger : 0

  const applyReveal = () => {
    if (once && hasRevealed) return
    hasRevealed = true

    revealNodes.forEach((node, index) => {
      if (delayStep) {
        node.style.transitionDelay = `${index * delayStep}ms`
      }
      node.classList.add(visibleClass)
    })

    if (typeof onReveal === 'function') {
      onReveal(revealNodes)
    }
  }

  if ('IntersectionObserver' in window) {
    observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0]
        const forwardOk = guardForward
          ? entry.boundingClientRect.top >= 0
          : true

        if (entry.isIntersecting && forwardOk) {
          applyReveal()
          if (once && observer) {
            observer.disconnect()
            observer = null
          }
        }
      },
      { threshold }
    )

    observer.observe(container)
  } else {
    const fallbackHandler = () => {
      if (once && hasRevealed) {
        if (cleanupFallback) {
          cleanupFallback()
          cleanupFallback = null
        }
        return
      }

      const rect = container.getBoundingClientRect()
      const vh =
        window.innerHeight || document.documentElement.clientHeight || 0

      if (rect.top <= vh * fallbackOffset && rect.bottom >= 0) {
        applyReveal()
        if (once && cleanupFallback) {
          cleanupFallback()
          cleanupFallback = null
        }
      }
    }

    cleanupFallback = () => {
      window.removeEventListener('scroll', fallbackHandler)
      window.removeEventListener('resize', fallbackHandler)
    }

    window.addEventListener('scroll', fallbackHandler, { passive: true })
    window.addEventListener('resize', fallbackHandler)
    fallbackHandler()
  }

  return () => {
    if (observer) {
      observer.disconnect()
      observer = null
    }
    if (cleanupFallback) {
      cleanupFallback()
      cleanupFallback = null
    }
  }
}

/**
 * 扫描整个页面的 data-scroll-reveal 元素，为每个元素单独绑定显现动画。
 * 适合简单标题/段落的通用场景，复杂区域请使用 setupScrollReveal 自行管理。
 * @param {Document|HTMLElement} [root=document]
 * @param {Object} [options]
 * @param {string} [options.selector='[data-scroll-reveal]:not([data-scroll-reveal="scoped"])']
 * @param {boolean} [options.guardForward=true]
 * @returns {() => void} 全量清理函数
 */
export function initGlobalScrollReveal(
  root = document,
  {
    selector = '[data-scroll-reveal]:not([data-scroll-reveal="scoped"])',
    guardForward = false
  } = {}
) {
  if (!root) return () => {}

  const elements = Array.from(root.querySelectorAll(selector))
  const cleanups = []

  elements.forEach((element) => {
    if (!element || element.dataset.scrollRevealBound === 'true') return
    element.dataset.scrollRevealBound = 'true'

    const dispose = setupScrollReveal(element, {
      targets: [element],
      visibleClass: 'scroll-reveal-visible',
      guardForward,
      stagger: 0
    })

    if (dispose) {
      cleanups.push(() => {
        dispose()
        delete element.dataset.scrollRevealBound
      })
    }
  })

  return () => {
    cleanups.forEach((cleanup) => cleanup && cleanup())
  }
}
